import { HttpParams } from '@angular/common/http';
import { DateUtils, TableFilterType } from '../../../shared';

export class OdataQueryOptions {
  filters: (ODataFilterGroup | OdataFilterExpression | OdataFilterString)[] = [];
  select: string[] = [];
  orderBy: SortExpression[] = [];
  skip?: number;
  top?: number;
  expand: string[] = [];
  count: boolean = false;

  constructor(init?: Partial<OdataQueryOptions>) {
    Object.assign(this, init);
  }

  buildODataFilter(expr: OdataFilterExpression | ODataFilterGroup | OdataFilterString): string {
    // Check if it's a FilterExpression
    if ('field' in expr) {
      return this.buildFilterExpressionString(expr);
    } else if ('filterString' in expr) {
      return expr.filterString;
    } else {
      const inner = expr.expressions.map(c => this.buildODataFilter(c)).join(` ${expr.logic} `);
      return `(${inner})`;
    }
  }

  /**
   * Merge this query with another one
   * - Filters are combined with AND
   * - OrderBy and Expand are concatenated (deduped)
   * - Skip/Top from the other query take precedence if defined
   */
  merge(other: OdataQueryOptions): OdataQueryOptions {
    const merged = new OdataQueryOptions({
      filters: [...this.filters, ...other.filters],
      orderBy: [...this.orderBy, ...other.orderBy],
      expand: Array.from(new Set([...this.expand, ...other.expand])),
      select: Array.from(new Set([...this.select, ...other.select])),
      skip: other.skip ?? this.skip,
      top: other.top ?? this.top,
      count: other.count ?? this.count,
    });

    return merged;
  }

  toHttpParams(): HttpParams {
    let params = new HttpParams();

    // $filter
    if (this.filters?.length) {
      const rootFilterGroup: ODataFilterGroup = {
        logic: 'and',
        expressions: this.filters,
      };
      params = params.set('$filter', this.buildODataFilter(rootFilterGroup));
    }

    // $orderby
    if (this.orderBy?.length) {
      const orderByString = this.orderBy.map(o => `${o.field} ${o.direction}`).join(', ');
      params = params.set('$orderby', orderByString);
    }

    // $expand
    if (this.expand?.length) {
      params = params.set('$expand', this.expand.join(','));
    }

    // $select
    if (this.select?.length) {
      params = params.set('$select', this.select.join(','));
    }

    // Paging
    if (this.skip !== undefined) {
      params = params.set('$skip', this.skip.toString());
    }
    if (this.top !== undefined) {
      params = params.set('$top', this.top.toString());
    }

    // Count
    if (this.count) {
      params = params.set('$count', 'true');
    }

    return params;
  }

  private buildFilterExpressionString(f: OdataFilterExpression): string {
    if (['contains', 'startswith', 'endswith'].includes(f.operator)) {
      // eslint-disable-next-line quotes
      return `${f.operator}(tolower(${f.field}), '${f.value.toString().toLowerCase().replace(/'/g, "''")}')`;
    }
    if (f.type === TableFilterType.dateOnly) {
      return `${f.field} ${f.operator} ${DateUtils.dateToIsoDateOnly(f.value as Date, true)}`;
    }
    if (f.value instanceof Date) {
      return `${f.field} ${f.operator} ${f.value.toISOString()}`;
    }
    if (typeof f.value === 'string') {
      // eslint-disable-next-line quotes
      return `${f.field} ${f.operator} '${f.value.replace(/'/g, "''")}'`;
    }
    return `${f.field} ${f.operator} ${f.value}`;
  }
}

export type OdataFilterOperator = 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le' | 'contains' | 'startswith' | 'endswith';

export type FilterExpressionValue = string | number | boolean | Date;

export interface OdataFilterExpression {
  field: string;
  operator: OdataFilterOperator;
  value: FilterExpressionValue;
  type?: TableFilterType;
}

export interface OdataFilterString {
  filterString: string;
}

export interface ODataFilterGroup {
  logic: 'and' | 'or';
  expressions: (OdataFilterExpression | ODataFilterGroup | OdataFilterString)[];
}

export interface SortExpression {
  field: string;
  direction: 'asc' | 'desc';
}
